import React, { useMemo, useRef, useState } from "react";

import * as d3 from "d3";

import {
  LogsCountPerSeverityOverviewData,
  LogsOverviewData
} from "Api/observability/pipeline/types";
import useUpdateDimension from "src/organization/pages/project/environment/metrics/http/hook/useUpdateDimension";
import Bar from "src/organization/pages/project/environment/metrics/logs/components/overview/Bar";
import { severityToColor } from "src/organization/pages/project/environment/metrics/logs/components/utils";
import {
  formatNumber,
  getDefaultDomain,
  getBandAxis
} from "src/organization/pages/project/environment/metrics/logs/helper";
import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";
import { getXTickCount } from "src/organization/pages/project/environment/metrics/utils/helper";

import LogsOverviewLayout from "../components/overview/LogsOverviewLayout";
import {
  LogOverviewPoint,
  PageTimeFrameType,
  severityLevels,
  StackedLogSeries
} from "../types";

import * as S from "./LogsOverview.styles";
import TooltipLogSummary from "./TooltipLogSummary";

type LogsOverviewProps = {
  data: LogsOverviewData[];
  timeframe: PageTimeFrameType;
};

const chartId = "logs-overview";

const getDomains = (data: StackedLogSeries[], timeframe?: TimeFrameType) => {
  const flatSeries = data.flat();
  const defaultDomain = getDefaultDomain(timeframe);

  const domains = {
    xDomain: flatSeries.map(d => new Date(d.data.timestamp * 1000)),
    yDomain: [0, d3.max(flatSeries, d => Math.max(d[0], d[1]))]
  };
  return flatSeries.length ? domains : defaultDomain;
};

type TooltipState = {
  point: LogOverviewPoint | null;
  showOnLeft: boolean;
  coords: {
    left: number;
    top: number;
  } | null;
};

const LogsOverview = ({ data, timeframe }: LogsOverviewProps) => {
  const tooltipRef = useRef<Element | null>(null);
  const dimension = useUpdateDimension(chartId);

  const [tooltipState, setTooltipState] = useState<TooltipState>({
    coords: null,
    point: null,
    showOnLeft: false
  });

  const handleCursorMove = (e, point) => {
    const pointer = d3.pointer(e, tooltipRef.current);
    const showOnLeft = dimension.width
      ? dimension.width - pointer[0] < 250
      : false;
    const left = pointer[0] + (showOnLeft ? 0 : 120);
    const top = pointer[1];

    const coords = {
      left,
      top
    };

    setTooltipState({
      coords,
      point,
      showOnLeft
    });
  };

  const handleCursorLeave = () => {
    setTooltipState({
      coords: {
        top: 0,
        left: 0
      },
      point: null,
      showOnLeft: false
    });
  };

  const stackedSeries = useMemo(() => {
    const stack = d3.stack<LogOverviewPoint>();
    const normalizedData: LogOverviewPoint[] = [];

    data.forEach(logAgg => {
      const severityCounts = {
        INFO: 0,
        DEBUG: 0,
        NOTICE: 0,
        WARNING: 0,
        ERROR: 0,
        FATAL: 0
      };

      let total = 0;
      if (logAgg.values) {
        logAgg.values.forEach((s: LogsCountPerSeverityOverviewData) => {
          severityCounts[s.severity] += s.count;
          total += s.count;
        });
      }

      normalizedData.push({
        timestamp: logAgg.timestamp,
        timestampDate: new Date(logAgg.timestamp * 1000),
        total,
        ...severityCounts
      });
    });

    return stack.keys(severityLevels)(normalizedData) as StackedLogSeries[];
  }, [data]);

  const { xDomain, yDomain } = useMemo(
    () => getDomains(stackedSeries, timeframe),
    [timeframe, stackedSeries]
  );

  const { xScale, yScale, positions } = getBandAxis({
    dimension: dimension,
    xDomain,
    yDomain
  });

  const disable =
    stackedSeries?.[0]?.length == 0 && stackedSeries?.[1]?.length == 0;
  const xtick = getXTickCount(dimension.width, timeframe, 17);

  return (
    <>
      <LogsOverviewLayout
        ref={tooltipRef}
        tick={{
          y: {
            format: (count: number) => formatNumber({ value: count })
          },
          x: xtick
        }}
        chartId={chartId}
        yScale={yScale}
        xScale={xScale}
        positions={positions}
      >
        {disable ? (
          <div>No Logs!</div>
        ) : (
          stackedSeries.map(data => {
            return data.map(d => {
              return (
                <Bar
                  chartId={chartId}
                  key={`${data.key}-${d.data.timestamp}`}
                  xScale={xScale}
                  yScale={yScale}
                  data={d}
                  fill={severityToColor(data.key)}
                  onMouseLeave={handleCursorLeave}
                  onMouseOver={handleCursorMove}
                ></Bar>
              );
            });
          })
        )}
      </LogsOverviewLayout>
      {tooltipState.coords && (
        <S.Tooltip
          showOnLeft={tooltipState.showOnLeft}
          style={{
            left: `${tooltipState.coords.left}px`,
            top: `${tooltipState.coords.top}px`
          }}
        >
          {tooltipState.point && (
            <TooltipLogSummary point={tooltipState.point}></TooltipLogSummary>
          )}
        </S.Tooltip>
      )}
    </>
  );
};

export default LogsOverview;
